import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TEAM_FACTION_NAMES, DISPLAY_TEAM_BORDER, DISPLAY_TEAM_TEXT, getDisplayTeamBg } from '../displayTheme';

interface SheetWinnerFullscreenProps {
  sessionId: string;
  activeTeam?: string; // For SEQUENTIAL mode - show only this team's winner
}

interface Sheet {
  id: string;
  name: string;
  description: string;
  imageUrl?: string;
  bonuses: Array<{ attribute: string; value: number }>;
  maluses: Array<{ attribute: string; value: number }>;
  moves: Array<{ name: string; description: string }>;
  notes?: string;
  damage?: number;
  armor?: number;
  maxHP?: number;
  level?: number;
}

interface WinnerData {
  teamColor: string;
  sheet: Sheet;
}

/**
 * Sheet Winner Fullscreen Display
 *
 * Mostra la scheda completa del vincitore in formato fullscreen (1920x1080)
 * con immagine, stats, bonus, malus e mosse tutte visibili senza scroll.
 */
function SheetWinnerFullscreen({ sessionId, activeTeam }: SheetWinnerFullscreenProps) {
  const { i18n } = useTranslation();
  const [winner, setWinner] = useState<WinnerData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchWinner();
  }, [sessionId, activeTeam]);

  const fetchWinner = async () => {
    try {
      // Fetch all sheet votes for this session
      const response = await fetch(`/api/sheet-votes/session/${sessionId}/all`);
      if (!response.ok) {
        setLoading(false);
        return;
      }

      const data = await response.json();
      const votes = data.votes || [];

      // Find the latest closed vote with a winner for the active team
      let latestVote: any = null;
      for (const vote of votes) {
        if (activeTeam && vote.teamColor !== activeTeam) continue;

        if (!vote.isActive && vote.finalSheetId && vote.closedAt) {
          if (!latestVote || new Date(vote.closedAt) > new Date(latestVote.closedAt)) {
            latestVote = vote;
          }
        }
      }

      if (latestVote) {
        // Fetch the full sheet details
        const sheetResponse = await fetch(`/api/sheets/${latestVote.finalSheetId}`);
        if (sheetResponse.ok) {
          const sheetData = await sheetResponse.json();
          setWinner({
            teamColor: latestVote.teamColor,
            sheet: sheetData.sheet,
          });
        }
      }
    } catch (error) {
      console.error('Failed to fetch winner:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center display-dark-bg">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-8 border-parchment-300 mx-auto mb-8"></div>
          <p className="text-3xl text-parchment-200">
            {i18n.language === 'it' ? 'Caricamento eroe...' : 'Loading hero...'}
          </p>
        </div>
      </div>
    );
  }

  if (!winner) {
    return (
      <div className="h-screen flex items-center justify-center display-dark-bg">
        <div className="text-center">
          <h1 className="text-5xl font-display font-bold text-parchment-100 mb-4">
            {i18n.language === 'it' ? 'Nessun Vincitore' : 'No Winner'}
          </h1>
          <p className="text-2xl text-parchment-400">
            {i18n.language === 'it' ? 'La votazione non è ancora conclusa' : 'Voting has not finished yet'}
          </p>
        </div>
      </div>
    );
  }

  const teamBorder = DISPLAY_TEAM_BORDER[winner.teamColor];
  const teamText = DISPLAY_TEAM_TEXT[winner.teamColor];
  const teamBgClass = getDisplayTeamBg(winner.teamColor);
  const { sheet } = winner;

  return (
    <div className={`h-screen w-screen ${teamBgClass} p-8 overflow-hidden`}>
      <div className="h-full flex gap-8">
        {/* Left Column - Image */}
        <div className="w-1/3 flex flex-col">
          {/* Team Badge */}
          <div className="text-center mb-4">
            <span className={`inline-block px-6 py-2 rounded-full display-panel border-2 ${teamBorder} ${teamText} text-2xl font-heading font-bold uppercase`}>
              {TEAM_FACTION_NAMES[winner.teamColor]}
            </span>
          </div>

          {/* Character Image */}
          <div className="flex-1 flex items-center justify-center">
            {sheet.imageUrl ? (
              <img
                src={sheet.imageUrl}
                alt={sheet.name}
                className={`max-w-full max-h-full object-contain rounded-2xl border-4 ${teamBorder} shadow-2xl`}
              />
            ) : (
              <div className={`w-full h-full flex items-center justify-center rounded-2xl border-4 ${teamBorder} bg-white/5`}>
                <span className="text-[12rem]">⚔️</span>
              </div>
            )}
          </div>
        </div>

        {/* Right Column - Details */}
        <div className="w-2/3 flex flex-col">
          {/* Character Name */}
          <div className="text-center mb-6">
            <h1 className="text-6xl font-display font-bold text-parchment-100 uppercase tracking-wide">
              {sheet.name}
            </h1>
            <p className="text-2xl text-parchment-200 font-body mt-2 line-clamp-2">
              {sheet.description}
            </p>
          </div>

          {/* Combat Stats */}
          <div className="grid grid-cols-4 gap-4 mb-6">
            <div className="display-panel p-3 text-center rounded-xl">
              <p className="text-sm text-parchment-400 uppercase tracking-wide">
                {i18n.language === 'it' ? 'Danno' : 'Damage'}
              </p>
              <p className="text-4xl font-display font-bold text-parchment-100">{sheet.damage ?? 0}</p>
            </div>
            <div className="display-panel p-3 text-center rounded-xl">
              <p className="text-sm text-parchment-400 uppercase tracking-wide">
                {i18n.language === 'it' ? 'Armatura' : 'Armor'}
              </p>
              <p className="text-4xl font-display font-bold text-parchment-100">{sheet.armor ?? 0}</p>
            </div>
            <div className="display-panel p-3 text-center rounded-xl">
              <p className="text-sm text-parchment-400 uppercase tracking-wide">
                {i18n.language === 'it' ? 'PF Max' : 'Max HP'}
              </p>
              <p className="text-4xl font-display font-bold text-parchment-100">{sheet.maxHP ?? 10}</p>
            </div>
            <div className="display-panel p-3 text-center rounded-xl">
              <p className="text-sm text-parchment-400 uppercase tracking-wide">
                {i18n.language === 'it' ? 'Livello' : 'Level'}
              </p>
              <p className="text-4xl font-display font-bold text-parchment-100">{sheet.level ?? 1}</p>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="flex-1 grid grid-cols-2 gap-6">
            {/* Bonuses */}
            <div className="display-panel p-6 border-l-4 border-green-400">
              <h3 className="text-3xl font-heading font-bold text-green-300 mb-4">
                {i18n.language === 'it' ? 'BONUS' : 'BONUSES'}
              </h3>
              <div className="space-y-3">
                {sheet.bonuses && sheet.bonuses.length > 0 ? (
                  sheet.bonuses.map((bonus, idx) => (
                    <div key={idx} className="flex justify-between items-center text-2xl">
                      <span className="text-parchment-100">{bonus.attribute}</span>
                      <span className="text-green-400 font-bold">+{bonus.value}</span>
                    </div>
                  ))
                ) : (
                  <p className="text-parchment-400 text-xl italic">
                    {i18n.language === 'it' ? 'Nessun bonus' : 'No bonuses'}
                  </p>
                )}
              </div>
            </div>

            {/* Maluses */}
            <div className="display-panel p-6 border-l-4 border-red-400">
              <h3 className="text-3xl font-heading font-bold text-red-300 mb-4">
                {i18n.language === 'it' ? 'MALUS' : 'MALUSES'}
              </h3>
              <div className="space-y-3">
                {sheet.maluses && sheet.maluses.length > 0 ? (
                  sheet.maluses.map((malus, idx) => (
                    <div key={idx} className="flex justify-between items-center text-2xl">
                      <span className="text-parchment-100">{malus.attribute}</span>
                      <span className="text-red-400 font-bold">{malus.value}</span>
                    </div>
                  ))
                ) : (
                  <p className="text-parchment-400 text-xl italic">
                    {i18n.language === 'it' ? 'Nessun malus' : 'No maluses'}
                  </p>
                )}
              </div>
            </div>

            {/* Moves - Full width */}
            <div className="col-span-2 display-panel p-6 border-l-4 border-parchment-300">
              <h3 className="text-3xl font-heading font-bold text-parchment-300 mb-4">
                {i18n.language === 'it' ? 'MOSSE SPECIALI' : 'SPECIAL MOVES'}
              </h3>
              <div className="grid grid-cols-2 gap-4">
                {sheet.moves && sheet.moves.length > 0 ? (
                  sheet.moves.map((move, idx) => (
                    <div key={idx} className="bg-white/5 rounded-xl p-4">
                      <h4 className="text-2xl font-bold text-parchment-100 mb-2">{move.name}</h4>
                      <p className="text-2xl text-parchment-200 line-clamp-3">{move.description}</p>
                    </div>
                  ))
                ) : (
                  <p className="text-parchment-400 text-xl italic col-span-2">
                    {i18n.language === 'it' ? 'Nessuna mossa speciale' : 'No special moves'}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SheetWinnerFullscreen;
